import { Injectable }             from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { map, Observable } from 'rxjs';

import { environment }                    from '@environment/environment';
import { TransformClass }                 from '@utils/transform';
import { ContactParams }                  from '@models/contact/contact-params';
import { PilotContactParams }             from '@models/contact/pilot-contact-params';
import { SearchComplaintsFilters }        from '@models/contact/search-complaint-filter';
import { RestPaginationAndSortingParams } from '@models/rest/rest-pagination-and-sorting-params';
import { RestPaged }                      from '@models/rest/rest-paged';
import { ComplaintsListItem }             from '@models/contact/complaints-list-item';
import { RestResponse }                   from '@models/rest';
import { Complaint }                      from '@models/contact/complaint';
import { ComplaintProofread }             from '@models/contact/complaint-proofread';
import { PostComplaint }      from '@models/contact/post-complaint';
import { getComplaintParams } from '@models/contact/get-complaints-params';
import { ComplaintSchool }    from '@shared/models/contact/complaint-school';
import { ComplaintSchoolList }            from '@shared/models/contact/complaint-school-list';
import { SearchSchoolComplaintFilter }    from '@shared/models/contact/search-complaint-school-filter';
import { ComplaintProofreaderInfo }       from '@models/contact/complaint-proofreader-info';


@Injectable({ providedIn: 'root' })
export class ApiContactService {
  private readonly baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = `${environment.apiUrl}/contato`;
  }

  public postContact(params: ContactParams): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}`, params);
  }

  public postPilotContact(params: PilotContactParams): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/piloto`, params);
  }

  public searchComplaintsList(
    filterParams: Partial<SearchComplaintsFilters> = {},
    paginationParams: RestPaginationAndSortingParams = { size: 100 }
  ): Observable<RestPaged<ComplaintsListItem[]>> {
    const httpParams: HttpParams = TransformClass.httpParams({
      ...filterParams,
      ...TransformClass.formatPaginationParams(paginationParams),
    });

    return this.http.get<RestPaged<ComplaintsListItem[]>>(
      `${this.baseUrl}/reclamacoes`,
      { params: httpParams }
    );
  }

  public getComplaint(complaintId: number): Observable<Complaint> {
    return this.http.get<RestResponse>(`${this.baseUrl}/reclamacao/${complaintId}`).pipe(
      map((response: RestResponse) => response.data as Complaint)
    );
  }

  public editComplaintProofreads(
    complaintId:         number,
    complaintProofreads: ComplaintProofread[]
  ): Observable<ComplaintProofread[]> {
    return this.http.post<RestResponse>(
      `${ this.baseUrl }/reclamacao/${ complaintId }/correcoes`, complaintProofreads
    ).pipe(map((response: RestResponse) => response.data as ComplaintProofread[]));
  }

  public newComplaint(complaint: PostComplaint): Observable<PostComplaint> {
    return this.http
      .post<RestResponse>(`${this.baseUrl}/reclamacao`, complaint)
      .pipe(map((response) => response.data as PostComplaint));
  }

  public editComplaint(complaint: PostComplaint): Observable<PostComplaint> {
    return this.http
      .put<RestResponse>(`${this.baseUrl}/reclamacao`, complaint)
      .pipe(map((response) => response.data as PostComplaint));
  }

  public deleteComplaint(reclamacaoId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/reclamacao/${reclamacaoId}`);
  }

  public getComplaintProofreaderInfo(proofreaderId: number): Observable<ComplaintProofreaderInfo> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/reclamacoes/${ proofreaderId }/info`).pipe(
      map((response) => response.data as ComplaintProofreaderInfo)
    );
  }

  public changeComplaintStatus(
    reclamacaoId: number,
    status:       string
  ): Observable<void> {
    const httpParams: HttpParams = TransformClass.httpParams({ status });

    return this.http.put<void>(
      `${this.baseUrl}/reclamacao/${reclamacaoId}/status`,
      {},
      { params: httpParams }
    );
  }

  public getComplaintExcel(params: getComplaintParams): Observable<Blob> {
    const endpoint: string       = `${ this.baseUrl }/reclamacao/relatorio/excel`,
          httpParams: HttpParams = TransformClass.httpParams(params),
          httpOptions: object    = {
            response:     'blob',
            responseType: 'blob',
            params:       httpParams
          };

    return this.http.get<Blob>(endpoint, httpOptions);
  }

  public getComplaintsSchool(filterParams: Partial<SearchSchoolComplaintFilter> = {}): Observable<ComplaintSchoolList[]> {

    const httpParams: HttpParams = TransformClass.httpParams({ ...filterParams });

    return this.http.get<RestResponse>(`${ this.baseUrl }/reclamacao/escola`, { params: httpParams })
               .pipe(map((response) => response.data as ComplaintSchoolList[]));
  }

  public sendComplaintSchool(complaint: ComplaintSchool): Observable<void> {
    return this.http.post<void>(`${ this.baseUrl }/reclamacao/escola`, complaint);
  }

}
